import {
  LoginFormCopy,
  ConfirmEmail,
  ConfirmEmailCopy,
  LoggedInView,
  LoggedInViewCopy,
  useSlideOverDialogContext,
  SocialLoginProps,
} from "@bluebottlecoffee/design-system/components";
import type { Result } from "@bluebottlecoffee/design-system/components/lib/types";
import { useUser, useAuth, useAnalytics } from "@chordcommerce/react-autonomy";
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import Script from "next/script";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { formatOTPError, useOTPError } from "../lib/hooks/use-otp-error";
import emailIsValid from "../lib/utils/strings/email-is-valid";
import { AccountMenuCopy, accountMenuLinks } from "../lib/account-menu";
import { isFeatureEnabled } from "../lib/utils/is-feature-enabled";
import { getIsClientSide } from "../lib/utils";
import { getTokenWithRefresh } from "../lib/auth/get-token-with-refresh";
import OTPForm from "./OTPForm";

export type OTPCallbackCopy = {
  login: string;
  pleaseSubmit: string;
  submit: string;
  success: string;
};

export type LoginCopy = LoginFormCopy & {
  facebookButtonText: string;
  googleButtonText: string;
  appleButtonText: string;
} & OTPCallbackCopy;

export interface DialogData {
  copy: LoginCopy;
  accountMenuCopy: AccountMenuCopy;
  region: string;
  lang: string;
  updateTitle?: (title: string) => void;
  focusDismissButton?: () => void;
}

export const Login: FunctionComponent<DialogData> = ({
  copy,
  accountMenuCopy,
  updateTitle,
  focusDismissButton,
  ...defaultProps
}) => {
  const { lang, region } = defaultProps;
  const { activeContentType, setActiveContentType } =
    useSlideOverDialogContext();
  const { user, loadUser } = useUser();
  const { logout, isLoggedIn, getToken } = useAuth();
  const [email, setEmail] = useState<string>("");
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLogOutLoading, setIsLogOutLoading] = useState<boolean>(false);
  const [isLogOutError, setIsLogOutError] = useState<boolean>(false);
  const [redirectUriOrigin, setRedirectUriOrigin] = useState<string>("");
  const confirmEmailRef = useRef<HTMLDivElement>(null);
  const previousIsLoggedInRef = useRef(isLoggedIn);
  const [otpCode, setOtpCode] = useState<string>("");
  const [otpSuccessMessage, setOtpSuccessMessage] = useState<string | null>(
    null,
  );
  const [otpError, setOtpError] = useState<string | null>(null);
  const handleOTPAuthError = useOTPError();
  const otpHeadingId = "login-otp-heading";

  useEffect(() => {
    if (emailSubmitted && confirmEmailRef.current) {
      confirmEmailRef.current.focus();
    }
  }, [emailSubmitted]);

  useEffect(() => {
    if (previousIsLoggedInRef.current && !isLoggedIn) {
      focusDismissButton?.();
    }
    previousIsLoggedInRef.current = isLoggedIn;
  }, [focusDismissButton, isLoggedIn]);

  const confirmEmailCopy: ConfirmEmailCopy = {
    confirmModalDismiss: copy.confirmModalDismiss,
    confirmModalTitle: copy.confirmModalTitle,
    confirmModalEmailConfirmation: copy.confirmModalEmailConfirmation,
    confirmModalInstructions: copy.confirmModalInstructions,
  };
  const loggedInCopy: LoggedInViewCopy = {
    loggedInGreeting: copy.loggedInGreeting,
    accountMenuHeading: copy.accountMenuHeading,
    logoutButton: copy.logoutButton,
    loggingOut: copy.loggingOut ?? "Logging out...",
    logOutError: copy.logOutError ?? "Logout failed. Please try again.",
  };

  const isAppleSocialLoginEnabled = isFeatureEnabled(
    process.env.NEXT_PUBLIC_ENABLE_APPLE_SOCIAL_LOGIN,
  );
  const isFacebookSocialLoginEnabled = isFeatureEnabled(
    process.env.NEXT_PUBLIC_ENABLE_FACEBOOK_SOCIAL_LOGIN,
  );
  const isGoogleSocialLoginEnabled = isFeatureEnabled(
    process.env.NEXT_PUBLIC_ENABLE_GOOGLE_SOCIAL_LOGIN,
  );
  const isSocialLoginEnabled =
    isAppleSocialLoginEnabled ||
    isFacebookSocialLoginEnabled ||
    isGoogleSocialLoginEnabled;

  const getSocialHref = (provider: string): string => {
    const base = process.env.NEXT_PUBLIC_COGNITO_HOST;
    const cognitoId = process.env.NEXT_PUBLIC_COGNITO_USER_POOL_WEB_CLIENT_ID;
    const encodedRedirectOrigin = encodeURIComponent(redirectUriOrigin);
    return `${base}/oauth2/authorize?client_id=${cognitoId}&identity_provider=${provider}&redirect_uri=${encodedRedirectOrigin}%2Foauth2%2Fcallback&response_type=code&scope=openid+email`;
  };

  const socialLoginProps: SocialLoginProps | undefined =
    // isSocialLoginEnabled prevents an empty object from being passed,
    // which currently results in component rendering an hr with the text "or"
    isSocialLoginEnabled && {
      ...(isFacebookSocialLoginEnabled && {
        facebook: {
          buttonText: copy.facebookButtonText,
          href: getSocialHref("Facebook"),
        },
      }),
      ...(isGoogleSocialLoginEnabled && {
        google: {
          buttonText: copy.googleButtonText,
          href: getSocialHref("Google"),
        },
      }),
      ...(isAppleSocialLoginEnabled && {
        apple: {
          buttonText: copy.appleButtonText,
          href: getSocialHref("SignInWithApple"),
        },
      }),
    };

  // Dynamic import of LoginForm component to avoid ReferenceError: window not defined
  // https://nextjs.org/docs/messages/react-hydration-error#solution-2-disabling-ssr-on-specific-components
  const LoginFormDynamic = dynamic(
    () =>
      import("@bluebottlecoffee/design-system/components/LoginForm/LoginForm").then(
        (mod) => mod.LoginForm,
      ),
    { ssr: false },
  );

  useEffect(() => {
    if (getIsClientSide()) {
      setRedirectUriOrigin(window.location.origin);
    }
  }, []);

  const loadTokenUser = async () => {
    try {
      await getTokenWithRefresh(getToken);
      await loadUser();
    } catch (error) {
      console.error("Error loading user with token:", error);
    }
  };

  useEffect(() => {
    if (activeContentType === "login") {
      loadTokenUser();
    }
  }, [activeContentType]);

  const { trackEmailCaptured } = useAnalytics();

  const onSubmit = async (email): Promise<Result> => {
    const errorStatus = (message?: string): Result => ({
      success: false,
      message: `${copy.unableToLogInMessage}${message ? `: ${message}` : ""}`,
    });
    setIsLoading(true);

    if (!emailIsValid(email)) {
      return Promise.resolve({
        success: false,
        message: copy.invalidEmailFormat,
      });
    }

    setEmail(email);
    trackEmailCaptured({
      emailCapture: {
        email,
      },
    });
    try {
      return new Promise((resolve) => {
        window.grecaptcha.ready(async () => {
          const token = await window.grecaptcha.execute(
            process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY,
            {
              action: "login",
            },
          );

          const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";

          const redirectUri = window.location.href;

          const loginResponse = await fetch(`${base}/api/v2/login`, {
            method: "POST",
            body: JSON.stringify({
              email,
              redirectUri,
              token,
            }),
          });

          const { message: errorMessage } = await loginResponse.json();

          if (loginResponse.status === 200) {
            setEmailSubmitted(true);
            setIsLoading(false);
          }

          resolve(errorStatus(errorMessage));
        });
      });
    } catch (error) {
      setIsLoading(false);
      return errorStatus(error);
    }
  };

  const handleLogout = async () => {
    setIsLogOutError(false);
    setIsLogOutLoading(true);
    try {
      await logout();
    } catch (error) {
      setIsLogOutLoading(false);
      setIsLogOutError(true);
      console.error("Logout failed:", error);
    }
    setIsLogOutLoading(false);
  };

  const handleOTPSubmit = async () => {
    setIsLoading(true);
    try {
      const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";

      const tokenResponse = await fetch(
        `${base}/api/v2/login/one-time-password`,
        {
          method: "POST",
          body: JSON.stringify({ code: otpCode, email }),
        },
      );

      if (tokenResponse.status === 200) {
        setOtpError(null);
        setOtpSuccessMessage(copy.success);
        // give it 2 secs for a11y success message to be read before closing the dialog
        setTimeout(() => {
          setActiveContentType(null);
        }, 2000);
      }

      const { status } = tokenResponse;

      const { AccessToken, IdToken, RefreshToken, error } =
        await tokenResponse.json();

      const errorStatus = `${status} ${formatOTPError(error)}`;

      if (status !== 200) {
        throw new Error(errorStatus, { cause: error });
      }

      if (!AccessToken || !IdToken || !RefreshToken) {
        throw new Error("Failed to get token", {
          cause: errorStatus,
        });
      }

      const accessToken = new CognitoAccessToken({
        AccessToken,
      });

      const idToken = new CognitoIdToken({
        IdToken,
      });

      const refreshToken = new CognitoRefreshToken({
        RefreshToken,
      });

      const session = new CognitoUserSession({
        IdToken: idToken,
        AccessToken: accessToken,
        RefreshToken: refreshToken,
      });

      const userPool = new CognitoUserPool({
        UserPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID,
        ClientId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_WEB_CLIENT_ID,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.setSignInUserSession(session);
    } catch (error) {
      handleOTPAuthError({
        error,
        email,
        code: otpCode,
        setOtpError,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderView = () => {
    if (!isLoggedIn) {
      if (email && emailSubmitted) {
        updateTitle?.(copy.confirmModalTitle);
        return (
          <>
            <div
              ref={confirmEmailRef}
              tabIndex={-1}
              className="focus:outline-none"
            >
              <ConfirmEmail copy={confirmEmailCopy} email={email} />
            </div>
            <OTPForm
              successMessage={otpSuccessMessage}
              errorMessage={otpError}
              pleaseSubmit={copy.pleaseSubmit}
              submitLabel={copy.submit}
              loadingText={copy.loadingText}
              code={otpCode}
              onCodeChange={setOtpCode}
              isLoading={isLoading}
              onSubmit={handleOTPSubmit}
              headingId={otpHeadingId}
            />
          </>
        );
      }

      updateTitle?.(copy.title);
      return (
        <>
          <LoginFormDynamic
            copy={copy}
            socialLoginProps={socialLoginProps}
            awaitingLinkClick={false}
            isLoading={isLoading}
            onSubmit={onSubmit}
          />
          <Script
            id="google-recaptcha"
            src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY}`}
          />
        </>
      );
    }

    updateTitle?.(loggedInCopy.accountMenuHeading);
    return (
      <LoggedInView
        copy={loggedInCopy}
        isLogOutLoading={isLogOutLoading}
        isLogOutError={isLogOutError}
        menuLinks={accountMenuLinks(accountMenuCopy, region, lang)}
        onLogOut={handleLogout}
        user={user.data}
      />
    );
  };
  return <>{renderView()}</>;
};
